import { useEffect, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * Clinics (033; canvas *Clinics Mockups*, approved 30 October). A clinic's own
 * page for a family, its direct link, and the coach's side.
 */

type GetToken = () => Promise<string | null>;

export interface ClinicFacts {
  clinic_id: string;
  title: string;
  description: string;
  image_url: string | null;
  slot_start: string;
  slot_end: string;
  coach_name: string;
  facility_name: string;
  places_free: number;
}

export interface FamilyClinic extends ClinicFacts {
  slot_id: string;
  service_id: string;
  tier_id: string;
  /** The family's athletes already in it: not offered again. */
  booked_ids: string[];
}

export interface ClinicAthlete {
  lesson_id: string;
  name: string;
  state: 'open' | 'credit' | 'no_credit' | 'closed';
  no_credit: boolean;
}

export interface CoachClinic extends ClinicFacts {
  facility_id: string;
  date: string;
  start: string;
  end: string;
  capacity: number;
  open_to_anyone: boolean;
  teams: { team_id: string; name: string }[];
  link_token: string;
  athletes: ClinicAthlete[];
}

export interface ClinicForm {
  title: string;
  description: string;
  facility_id: string;
  date: string;
  start: string;
  end: string;
  places: number;
  open_to_anyone: boolean;
  team_ids: string[];
  /** A new picture, base64; left out to keep the one there is. */
  image?: string;
  /** Copy clinic: use that clinic's picture. */
  image_from?: string;
}

export type ClinicState<T> =
  | { status: 'loading' }
  | { status: 'ready'; clinic: T }
  | { status: 'gone' }
  | { status: 'failed'; message: string };

/** A read that treats 404 as a fact about the clinic and nothing else as one (use-slot.ts). */
export function useClinicRead<T>(read: (() => Promise<T>) | null, key: string): { state: ClinicState<T>; reload: () => void } {
  const [state, setState] = useState<ClinicState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (read === null) return;
    let cancelled = false;
    read()
      .then((clinic) => {
        if (!cancelled) setState({ status: 'ready', clinic });
      })
      .catch((cause: unknown) => {
        if (cancelled || cause instanceof NotSignedIn) return;
        if (cause instanceof ApiError && cause.status === 404) setState({ status: 'gone' });
        else setState({ status: 'failed', message: cause instanceof ApiError ? cause.message : 'That did not load.' });
      });
    return () => {
      cancelled = true;
    };
    // `read` is a new function every render; `key` says what it reads.
  }, [key, nonce, read === null]);

  return { state, reload: () => setNonce((n) => n + 1) };
}

export function useFamilyClinic(clinicId: string | undefined) {
  const getToken = useAccessToken();
  return useClinicRead(() => apiFetch<FamilyClinic>(`/api/clinics/${clinicId ?? ''}`, getToken), clinicId ?? '');
}

export function useCoachClinic(clinicId: string | undefined) {
  const getToken = useAccessToken();
  return useClinicRead(clinicId ? () => readCoachClinic(getToken, clinicId) : null, clinicId ?? '');
}

export function readCoachClinic(getToken: GetToken, clinicId: string): Promise<CoachClinic> {
  return apiFetch(`/api/coach/clinics/${clinicId}`, getToken);
}

/** Signed out: the portal names itself, as an invitation's link does. */
export async function readClinicLink(token: string): Promise<ClinicFacts> {
  const response = await fetch(`/api/clinic-links/${encodeURIComponent(token)}`, {
    headers: { 'x-portal-client-id': String(import.meta.env['VITE_ENTRA_CLIENT_ID'] ?? '') },
  });
  if (response.status === 404) throw new ApiError(404, 'not_found', 'That clinic has gone.');
  if (!response.ok) throw new ApiError(response.status, `http_${response.status}`, 'That did not load.');
  return (await response.json()) as ClinicFacts;
}

export function openClinicLink(getToken: GetToken, token: string): Promise<{ clinic_id: string }> {
  return apiFetch(`/api/clinic-links/${encodeURIComponent(token)}/opens`, getToken, { method: 'POST' });
}

export function publishClinic(getToken: GetToken, form: ClinicForm): Promise<{ clinic_id: string }> {
  return apiFetch('/api/coach/clinics', getToken, { method: 'POST', body: JSON.stringify(form) });
}

export function saveClinic(getToken: GetToken, clinicId: string, form: ClinicForm): Promise<{ clinic_id: string }> {
  return apiFetch(`/api/coach/clinics/${clinicId}`, getToken, { method: 'PUT', body: JSON.stringify(form) });
}

export function checkIn(getToken: GetToken, clinicId: string, lessonId: string): Promise<unknown> {
  return apiFetch(`/api/coach/clinics/${clinicId}/check-ins`, getToken, {
    method: 'POST',
    body: JSON.stringify({ lesson_id: lessonId }),
  });
}

/** The address the coach copies and sends by hand. */
export function clinicLink(token: string): string {
  return `${window.location.origin}/clinics/${token}`;
}

/** "9:00 am – 12:00 pm" */
export function clinicTimes(startIso: string, endIso: string): string {
  const time = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
  return `${time(startIso)} – ${time(endIso)}`;
}

/** "Saturday 14 November" */
export function clinicDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function placesLabel(free: number): string {
  if (free <= 0) return 'Full';
  return `${free} place${free === 1 ? '' : 's'}`;
}
